import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router";
import { jobApprovals, jobApprovalKeys, type CrossJobApprovalRowDto } from "@/lib/job-approvals";
import { ListPage } from "@/archetypes/ListPage";
import { DataGrid, type DataGridColumn, type SortDirection } from "@/components/data-grid";

export function JobApprovalsPage() {
  const { data = [], isLoading } = useQuery({
    queryKey: jobApprovalKeys.cross({ status: "pending" }),
    queryFn: () => jobApprovals.cross({ status: "pending" }),
  });

  const [sort, setSort] = useState<{ columnId: string; direction: SortDirection } | null>(null);

  const visibleRows = useMemo(() => {
    if (!sort) return data;
    const dir = sort.direction === "asc" ? 1 : -1;
    const valueFor = (r: CrossJobApprovalRowDto): string | number | null => {
      switch (sort.columnId) {
        case "job":         return r.jobReferenceNo;
        case "client":      return r.clientName ?? null;
        case "type":        return r.approvalTypeLabel;
        case "requestedBy": return r.requestedByUserName;
        case "age":         return r.ageDays;
        case "status":      return r.currentJobStatusCode;
        default:            return null;
      }
    };
    return [...data].sort((a, b) => {
      const va = valueFor(a);
      const vb = valueFor(b);
      if (va == null && vb == null) return 0;
      if (va == null) return 1;
      if (vb == null) return -1;
      if (typeof va === "number" && typeof vb === "number") return (va - vb) * dir;
      return String(va).localeCompare(String(vb)) * dir;
    });
  }, [data, sort]);

  const cols: DataGridColumn<CrossJobApprovalRowDto>[] = [
    {
      id: "job",
      header: "Job",
      sortable: true,
      accessor: (r) => (
        <Link to={`/jobs/${r.jobId}`} className="underline text-primary">
          {r.jobReferenceNo}
        </Link>
      ),
    },
    { id: "client",      header: "Client",       sortable: true, accessor: (r) => r.clientName ?? "—" },
    { id: "type",        header: "Type",         sortable: true, accessor: (r) => r.approvalTypeLabel },
    { id: "requestedBy", header: "Requested by", sortable: true, accessor: (r) => r.requestedByUserName },
    { id: "age",         header: "Age",          sortable: true, accessor: (r) => `${r.ageDays}d`, align: "right" },
    { id: "status",      header: "Status",       sortable: true, accessor: (r) => r.currentJobStatusCode },
  ];

  return (
    <ListPage title="Pending Job Approvals">
      <DataGrid
        rows={visibleRows}
        columns={cols}
        rowKey={(r) => r.jobApprovalId}
        totalRows={data.length}
        pageIndex={0}
        pageSize={data.length || 1}
        loading={isLoading}
        emptyHeadline="No pending approvals."
        onPageChange={() => {}}
        onSortChange={setSort}
      />
    </ListPage>
  );
}
